<template>
  <div class="province-selector">
    <!-- Page Header -->
    <PageHeader
      :total-order-count="totalOrderCount"
      :active-provinces-count="activeProvincesCount"
      :last-update-time="lastUpdateTime"
      :is-updating="isUpdating"
    />

    <!-- Search and Filter Bar -->
    <SearchFilterBar
      :search-query.sync="search"
      :show-only-with-orders="showOnlyWithOrders"
      :show-only-pinned="showOnlyPinned"
      :grid-display.sync="gridDisplay"
      :is-refreshing="isUpdating"
      @toggle-orders-filter="toggleOrdersFilter"
      @toggle-pinned-filter="togglePinnedFilter"
      @refresh-data="refreshOrderData"
      class="search-filter-bar"
    />

    <!-- Province Grid/List -->
    <div class="provinces-container">
      <!-- Loading States -->
      <div
        v-if="isOrderDataLoading && showOnlyWithOrders"
        class="loading-container"
      >
        <v-progress-circular
          indeterminate
          color="#fddf00"
          size="64"
        ></v-progress-circular>
        <p class="loading-text">Loading order data...</p>
      </div>

      <div
        v-else-if="
          filteredProvince.length === 0 && !showOnlyWithOrders && !isDataLoaded
        "
        class="loading-container"
      >
        <v-progress-circular
          indeterminate
          color="#fddf00"
          size="64"
        ></v-progress-circular>
        <p class="loading-text">Loading provinces...</p>
      </div>

      <!-- Empty States -->
      <EmptyState
        v-else-if="
          filteredProvince.length === 0 && showOnlyWithOrders && !showOnlyPinned
        "
        type="no-orders"
        @show-all="showOnlyWithOrders = false"
      />

      <EmptyState
        v-else-if="filteredProvince.length === 0 && showOnlyPinned"
        type="no-pinned"
        @show-all="showOnlyPinned = false"
      />

      <!-- Province Display -->
      <div
        v-else
        class="province-display"
        :class="`province-display--${gridDisplay}`"
      >
        <!-- Grid View -->
        <template v-if="gridDisplay !== 'list'">
          <ProvinceCard
            v-for="(province, index) in filteredProvince"
            :key="`province-${index}`"
            :province="province"
            :order-amount="getOrderAmount(province.province_id)"
            :is-pinned="isPinned(province.province_id)"
            :is-loading="
              fetching_data && selectedProvinceId === province.province_id
            "
            @select="selectProvince"
            @toggle-pin="togglePin"
          />
        </template>

        <!-- List View -->
        <template v-else>
          <ProvinceListItem
            v-for="(province, index) in filteredProvince"
            :key="`province-list-${index}`"
            :province="province"
            :order-amount="getOrderAmount(province.province_id)"
            :is-pinned="isPinned(province.province_id)"
            :is-loading="
              fetching_data && selectedProvinceId === province.province_id
            "
            @select="selectProvince"
            @toggle-pin="togglePin"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {
  backendVersion,
  axiosPath,
  resolveBackendUrl,
} from "@/axios-path.js";
import { mapState } from "vuex";
import {
  provinceFilterMixin,
  localStorageMixin,
  timeFormattingMixin,
} from "@/mixins/homePageMixins.js";

// Import components
import PageHeader from "@/components/back-office/HomePage/PageHeader.vue";
import SearchFilterBar from "@/components/back-office/HomePage/SearchFilterBar.vue";
import ProvinceCard from "@/components/back-office/HomePage/ProvinceCard.vue";
import ProvinceListItem from "@/components/back-office/HomePage/ProvinceListItem.vue";
import EmptyState from "@/components/back-office/HomePage/EmptyState.vue";

export default {
  name: "HomePage",
  components: {
    PageHeader,
    SearchFilterBar,
    ProvinceCard,
    ProvinceListItem,
    EmptyState,
  },
  mixins: [provinceFilterMixin, localStorageMixin, timeFormattingMixin],
  data() {
    return {
      interval_order: null,
      fetching_data: false,
      search: "",
      isUpdating: false,
      lastUpdateTime: "",
      gridDisplay: "5",
      showOnlyWithOrders: false,
      selectedProvinceId: null,
      isOrderDataLoading: false,
      isDataLoaded: false,
      pinnedProvinces: [],
      showOnlyPinned: false,
    };
  },
  computed: {
    ...mapState({
      permission: (state) => state.permission,
      provinceData: (state) => state.fetchProvinceBack.provinceData,
      orderCountEachProvince: (state) =>
        state.fetchOrderListBack.orderCountEachProvince,
      province: (state) => state.backOffice.province,
      selectProvinceId: (state) => state.backOffice.selectProvinceId,
    }),

    /**
     * Filter provinces based on user permissions and search query
     */
    filterSearchProvince() {
      return this.provinceData
        .filter((province) => this.hasProvincePermission(province))
        .filter(
          (province) =>
            this.filterProvincesBySearch([province], this.search).length > 0
        );
    },

    /**
     * Apply all filters and sorting to provinces
     */
    filteredProvince() {
      let provinces = [...this.filterSearchProvince];

      // Apply pin filter if enabled
      if (this.showOnlyPinned) {
        provinces = provinces.filter((province) =>
          this.pinnedProvinces.includes(province.province_id)
        );
      }

      // Apply order filter if enabled
      if (this.showOnlyWithOrders) {
        provinces = provinces.filter(
          (province) => this.getOrderAmount(province.province_id) > 0
        );
      }

      // Sort pinned provinces to the top when not filtering specifically
      if (!this.showOnlyPinned && !this.showOnlyWithOrders) {
        provinces = this.sortProvincesByPinned(provinces, this.pinnedProvinces);
      }

      return provinces;
    },

    /**
     * Get total order count across all provinces
     */
    totalOrderCount() {
      return this.orderCountEachProvince.reduce((total, province) => {
        return total + (province.order_amount || 0);
      }, 0);
    },

    /**
     * Get count of provinces with active orders
     */
    activeProvincesCount() {
      return this.orderCountEachProvince.filter(
        (province) => province.order_amount > 0
      ).length;
    },
  },

  methods: {
    /**
     * Navigate to service management page
     */
    async selectService(info) {
      this.$router.push({ path: info.href });
    },

    /**
     * Handle province selection
     */
    async selectProvince(province) {
      if (this.fetching_data || !province.province_name_en) return;

      this.selectedProvinceId = province.province_id;
      const provinceSplited = this.splitProvince(province.province_name_en);
      this.fetching_data = true;

      try {
        await this.$store.dispatch("getAPIList", provinceSplited.toLowerCase());

        const domain = resolveBackendUrl(
          `${province.province_domain}/${
            backendVersion.version
          }/${provinceSplited.toLowerCase()}`
        );
        axiosPath.defaults.baseURL = domain;

        await this.$store.dispatch(
          "backOffice/selectProvince",
          provinceSplited.toLowerCase()
        );

        this.$router.push({
          path: `/weserve_admin_login/select/${provinceSplited.toLowerCase()}/`,
        });
      } catch (error) {
        console.error("Error selecting province:", error);
      } finally {
        this.fetching_data = false;
        this.selectedProvinceId = null;
      }
    },

    /**
     * Fetch initial province data
     */
    async fetchData() {
      try {
        await this.$store.dispatch("fetchProvinceBack/getData");
      } catch (error) {
        console.error("Error fetching province data:", error);
      }
    },

    /**
     * Refresh order data with loading state
     */
    async refreshOrderData() {
      this.isUpdating = true;
      await this.fetchOrderData();
      this.updateLastUpdateTime();

      setTimeout(() => {
        this.isUpdating = false;
      }, 1000);
    },

    /**
     * Update the last update time display
     */
    updateLastUpdateTime() {
      this.lastUpdateTime = this.getCurrentTime();
    },

    /**
     * Fetch order count data for all provinces
     */
    async fetchOrderData() {
      if (!this.isDataLoaded) {
        this.isOrderDataLoading = true;
      }

      try {
        await this.$store.dispatch(
          "fetchOrderListBack/fetchOrderCountEachProvince"
        );
      } catch (error) {
        console.error("Error fetching order data:", error);
      } finally {
        this.isOrderDataLoading = false;
        this.isDataLoaded = true;
      }
    },

    /**
     * Toggle pin status for a province
     */
    togglePin(province) {
      const provinceId = province.province_id;
      const index = this.pinnedProvinces.indexOf(provinceId);

      if (index === -1) {
        this.pinnedProvinces.push(provinceId);
      } else {
        this.pinnedProvinces.splice(index, 1);
      }

      this.savePinnedProvinces();
      this.animatePinButton(provinceId);
    },

    /**
     * Check if province is pinned
     */
    isPinned(provinceId) {
      return this.pinnedProvinces.includes(provinceId);
    },

    /**
     * Save pinned provinces to localStorage
     */
    savePinnedProvinces() {
      this.saveToLocalStorage("pinnedProvinces", this.pinnedProvinces);
    },

    /**
     * Load pinned provinces from localStorage
     */
    loadPinnedProvinces() {
      this.pinnedProvinces = this.loadFromLocalStorage("pinnedProvinces", []);
    },

    /**
     * Add animation to pin button on toggle
     */
    animatePinButton(provinceId) {
      this.$nextTick(() => {
        const pinButtons = document.querySelectorAll(
          `[data-province-id="${provinceId}"] .pin-btn-list`
        );
        pinButtons.forEach((btn) => {
          btn.classList.add("pinned-animation");
          setTimeout(() => {
            btn.classList.remove("pinned-animation");
          }, 400);
        });
      });
    },

    /**
     * Toggle orders filter
     */
    toggleOrdersFilter() {
      this.showOnlyWithOrders = !this.showOnlyWithOrders;
    },

    /**
     * Toggle pinned filter
     */
    togglePinnedFilter() {
      this.showOnlyPinned = !this.showOnlyPinned;
    },

    /**
     * Legacy method for backward compatibility
     * @deprecated Use getOrderAmount instead
     */
    showOrderAmount(provinceId) {
      return this.getOrderAmount(provinceId);
    },
  },

  async created() {
    await this.fetchData();

    // Fetch order data if user has permission
    if (this.permission.order && this.permission.order.edit_view) {
      await this.fetchOrderData();
      this.updateLastUpdateTime();

      // Set up periodic order data refresh
      this.interval_order = setInterval(() => {
        this.fetchOrderData();
        this.updateLastUpdateTime();
      }, 30000);
    }
  },

  mounted() {
    // Initialize breadcrumb and store state
    const path = [{ text: "Select province", disabled: true }];
    this.$store.dispatch("backOffice/selectProvinceParam", null);
    this.$store.commit("backOffice/saveRecentRoute", path);
    this.$store.commit("backOffice/showMenu", null);

    // Load saved preferences
    this.loadPinnedProvinces();
  },

  destroyed() {
    // Clean up interval
    if (this.interval_order) {
      clearInterval(this.interval_order);
    }
  },
};
</script>

<style scoped>
.province-selector {
  min-height: 100vh;
  background: transparent;
}

.search-filter-bar {
  margin-top: -1rem;
  margin-bottom: 1rem;
}

.provinces-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem 1rem 2rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  gap: 1rem;
}

.loading-text {
  font-size: 1.1rem;
  color: white;
}

.province-display {
  transition: all 0.3s ease;
}

.province-display--5 {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 2rem;
}

.province-display--3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.province-display--list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Responsive Grid Layout */
@media (max-width: 1400px) {
  .province-display--5 {
    grid-template-columns: repeat(4, 1fr);
  }

  .province-display--3 {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 1200px) {
  .province-display--5 {
    grid-template-columns: repeat(3, 1fr);
  }

  .province-display--3 {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 900px) {
  .province-display--5,
  .province-display--3 {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .province-display--5,
  .province-display--3 {
    grid-template-columns: 1fr;
  }

  .provinces-container {
    padding: 0 0.5rem 2rem;
  }
}

/* Pin button animation */
@keyframes pinSuccess {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

.pinned-animation {
  animation: pinSuccess 0.4s ease;
}
</style>
